import { Address, zeroAddress } from 'viem';

export interface DexConfigBase {
  name: string;
  url: string;
  /**
   * DEX 协议类型
   */
  protocol: 'UniswapV2' | 'UniswapV3' | 'UniswapV4';

  /**
   * WETH 合约地址
   * 注意：不同链的 WETH 地址不同
   */
  WETH: `0x${string}`;

  /**
   * 可信任的 token 列表，用于中间兑换，比如 A -> WETH -> B
   */
  trustedTokens: `0x${string}`[];
}

export interface DexConfigV2 extends DexConfigBase {
  protocol: 'UniswapV2';
  /**
   * Factory 合约地址
   * https://docs.uniswap.org/contracts/v2/reference/smart-contracts/factory
   */
  factory: `0x${string}`;

  /**
   * Factory 合约的 bytecode keccak256 hash
   * provider.getCode(contractAddress).then((bytecode) => ethers.keccak256(bytecode))
   */
  bytecodeHash: `0x${string}`;

  /**
   * Router 合约地址
   * https://docs.uniswap.org/contracts/v2/reference/smart-contracts/router-02
   */
  router: `0x${string}`;
}

export interface DexConfigV3 extends DexConfigBase {
  protocol: 'UniswapV3';
  /**
   * Factory 合约地址
   */
  factory: `0x${string}`;

  /**
   * SwapRouter 合约地址，用于执行 swap
   * https://docs.uniswap.org/contracts/v3/reference/deployments
   */
  swapRouter: `0x${string}`;

  /**
   * QuoterV2 合约地址，用于价格查询
   */
  quoter: `0x${string}`;

  permit2: `0x${string}`;

  /**
   * 支持的费率等级 (0.01%, 0.05%, 0.3%, 1%)
   */
  feeTiers: number[];
}

export interface DexConfigV4 extends DexConfigBase {
  protocol: 'UniswapV4';
  /**
   * PoolManager 合约地址
   */
  poolManager: `0x${string}`;

  /**
   * SwapRouter 合约地址，用于执行 swap
   */
  swapRouter: `0x${string}`;

  /**
   * Quoter 合约地址，用于价格查询
   */
  quoter: `0x${string}`;

  /**
   * Permit2 合约地址，用于代币授权
   */
  permit2: `0x${string}`;

  /**
   * 支持的费率等级
   */
  feeTiers: number[];

  /**
   * Hooks 合约地址（如果有）
   */
  hooks?: `0x${string}`;
}

export type DexConfig = DexConfigV2 | DexConfigV3 | DexConfigV4;

const trustedTokenList: Address[] = ['0xbB69b049B4145663C9fA6cFEADFf42622d5601b6', '0x6ef691A7F1E0B6e017abB9d32324BCa16F7776Fb'];

export const DexUniswapV2: DexConfigV2 = {
  name: 'Uniswap v2',
  url: 'https://uniswap.org',
  protocol: 'UniswapV2',
  factory: '0xF62c03E08ada871A0bEb309762E260a7a6a880E6',
  bytecodeHash: '0x96e8ac4277198ff8b6f785478aa9a39f403cb768dd02cbee326c3e7da348845f',
  WETH: '0xfFf9976782d46CC05630D1f6eBAb18b2324d6B14', // Sepolia WETH
  router: '0xeE567Fe1712Faf6149d80dA1E6934E354124CfE3',
  trustedTokens: [
    '0xfFf9976782d46CC05630D1f6eBAb18b2324d6B14', // WETH
    ...trustedTokenList,
  ],
};

export const DexUniswapV3: DexConfigV3 = {
  name: 'Uniswap v3',
  url: 'https://uniswap.org',
  protocol: 'UniswapV3',
  factory: '0x0227628f3F023bb0B980b67D528571c95c6DaC1c', // Sepolia Factory
  // Universal Router 支持 V2/V3/V4，通过不同的 commands 区分
  // 参考: https://sepolia.etherscan.io/tx/0xa530f03fb86b9ab7de0dd89425cae3b47a2764f6455f8763d585b33171216b22
  swapRouter: '0x3A9D48AB9751398BbFa63ad67599Bb04e4BdF98b', // Universal Router Sepolia
  quoter: '0xEd1f6473345F45b75F8179591dd5bA1888cf2FB3', // QuoterV2 Sepolia
  permit2: '0x000000000022D473030F116dDEE9F6B43aC78BA3', // Permit2 合约地址（所有链通用）
  WETH: '0xfFf9976782d46CC05630D1f6eBAb18b2324d6B14', // Sepolia WETH
  feeTiers: [100, 500, 3000, 10000], // 0.01%, 0.05%, 0.3%, 1%
  trustedTokens: [
    '0xfFf9976782d46CC05630D1f6eBAb18b2324d6B14', // WETH
    ...trustedTokenList,
  ],
};

export const DexUniswapV4: DexConfigV4 = {
  name: 'Uniswap v4',
  url: 'https://uniswap.org',
  protocol: 'UniswapV4',
  poolManager: '0xE03A1074c86CFeDd5C142C4F04F1a1536e203543', // Sepolia PoolManager
  swapRouter: '0x3A9D48AB9751398BbFa63ad67599Bb04e4BdF98b', // Universal Router Sepolia
  quoter: '0x61b3f2011a92d183c7dbadbda940a7555ccf9227', // Sepolia Quoter
  permit2: '0x000000000022D473030F116dDEE9F6B43aC78BA3', // Permit2 合约地址（所有链通用）
  WETH: zeroAddress, // V4 uses native ETH (address(0))
  feeTiers: [100, 500, 3000, 10000], // 0.01%, 0.05%, 0.3%, 1%
  trustedTokens: [
    zeroAddress, // Native ETH
    ...trustedTokenList,
  ],
};

// https://docs.uniswap.org/contracts/v2/reference/smart-contracts/v2-deployments
export const DexMainnetUniswapV2: DexConfigV2 = {
  name: 'Uniswap v2',
  url: 'https://uniswap.org',
  protocol: 'UniswapV2',
  factory: '0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f',
  // https://docs.uniswap.org/contracts/v2/guides/smart-contract-integration/getting-pair-addresses
  bytecodeHash: '0x96e8ac4277198ff8b6f785478aa9a39f403cb768dd02cbee326c3e7da348845f',
  WETH: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', // WETH
  router: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D',
  trustedTokens: [
    '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', // WETH
    '0xdAC17F958D2ee523a2206206994597C13D831ec7', // USDT
    '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', // USDC
  ],
};

export const DexMainnetUniswapV3: DexConfigV3 = {
  name: 'Uniswap v3',
  url: 'https://uniswap.org',
  protocol: 'UniswapV3',
  factory: '0x1F98431c8aD98523631AE4a59f267346ea31F984', // Mainnet Factory
  // Universal Router 支持 V2/V3/V4，通过不同的 commands 区分
  // 参考: https://sepolia.etherscan.io/tx/0xa530f03fb86b9ab7de0dd89425cae3b47a2764f6455f8763d585b33171216b22
  swapRouter: '0x66a9893cc07d91d95644aedd05d03f95e1dba8af', // Universal Router Mainnet
  quoter: '0x61fFE014bA17989E743c5F6cB21bF9697530B21e', // QuoterV2 Mainnet
  permit2: '0x000000000022D473030F116dDEE9F6B43aC78BA3', // Permit2 合约地址（所有链通用）
  WETH: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', // Mainnet WETH
  feeTiers: [100, 500, 3000, 10000], // 0.01%, 0.05%, 0.3%, 1%
  trustedTokens: [
    '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', // WETH
    '0xdAC17F958D2ee523a2206206994597C13D831ec7', // USDT
    '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', // USDC
  ],
};

export const DexMainnetUniswapV4: DexConfigV4 = {
  name: 'Uniswap v4',
  url: 'https://uniswap.org',
  protocol: 'UniswapV4',
  poolManager: '0x000000000004444c5dc75cB358380D2e3dE08A90', // Mainnet PoolManager
  swapRouter: '0x66a9893cc07d91d95644aedd05d03f95e1dba8af', // Universal Router Mainnet
  quoter: '0x52f0e24d1c21c8a0cb1e5a5dd6198556bd9e1203', // Mainnet Quoter
  permit2: '0x000000000022D473030F116dDEE9F6B43aC78BA3', // Permit2 合约地址（所有链通用）
  WETH: zeroAddress, // V4 uses native ETH (address(0))
  feeTiers: [100, 500, 3000, 10000], // 0.01%, 0.05%, 0.3%, 1%
  trustedTokens: [
    zeroAddress, // Native ETH
    '0xdAC17F958D2ee523a2206206994597C13D831ec7', // USDT
    '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', // USDC
  ],
};

export const DexPancakeSwap: DexConfigV2 = {
  name: 'PancakeSwap v2',
  url: 'https://pancakeswap.finance',
  protocol: 'UniswapV2',
  factory: '0x6725F303b657a9451d8BA641348b6761A6CC7a17',
  bytecodeHash: '0xd0d4c4cd0848c93cb4fd1f498d7013ee6bfb25783ea21593d5834f5d250ece66',
  WETH: '0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd', // BSC Testnet WBNB
  router: '0xD99D1c33F9fC3444f8101754aBC46c52416550D1',
  trustedTokens: [
    '0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd', // WBNB
    ...trustedTokenList,
  ],
};

export const DexPancakeSwapV3: DexConfigV3 = {
  name: 'PancakeSwap v3',
  url: 'https://pancakeswap.finance',
  protocol: 'UniswapV3',
  factory: '0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865',
  swapRouter: '0x87FD5305E6a40F378da124864B2D479c2028BD86', // UniversalRouter on BSC Testnet
  quoter: '0xbC203d7f83677c7ed3F7acEc959963E7F4ECC5C2',
  permit2: '0x31c2F6fcFf4F8759b3Bd5Bf0e1084A055615c768',
  WETH: '0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd', // BSC Testnet WBNB
  // 注意：PancakeSwap V3 费率与 Uniswap V3 不同
  // PancakeSwap V3: 0.01%, 0.05%, 0.25%, 1%
  // Uniswap V3: 0.01%, 0.05%, 0.3%, 1%
  feeTiers: [100, 500, 2500, 10000], // 0.01%, 0.05%, 0.25%, 1%
  trustedTokens: [
    '0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd', // WBNB
    ...trustedTokenList,
  ],
};

export const DexBscPancakeSwap: DexConfigV2 = {
  name: 'PancakeSwap v2',
  url: 'https://pancakeswap.finance',
  protocol: 'UniswapV2',
  factory: '0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73',
  bytecodeHash: '0x00fb7f630766e6a796048ea87d01acd3068e8ff67d078148a3fa3f4a84f69bd5',
  WETH: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', // BSC WBNB
  router: '0x10ED43C718714eb63d5aA57B78B54704E256024E',
  trustedTokens: [
    '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', // WBNB
    '0x55d398326f99059fF775485246999027B3197955', // USDT
    '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', // USDC
  ],
};

export const DexBscPancakeSwapV3: DexConfigV3 = {
  name: 'PancakeSwap v3',
  url: 'https://pancakeswap.finance',
  protocol: 'UniswapV3',
  factory: '0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865',
  swapRouter: '0xd9C500DfF816a1Da21A48A732d3498Bf09dc9AEB', // UniversalRouter on BSC
  quoter: '0xB048Bbc1Ee6b733FFfCFb9e9CeF7375518e25997',
  permit2: '0x31c2F6fcFf4F8759b3Bd5Bf0e1084A055615c768',
  WETH: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', // BSC WBNB
  // 注意：PancakeSwap V3 费率与 Uniswap V3 不同
  // PancakeSwap V3: 0.01%, 0.05%, 0.25%, 1%
  // Uniswap V3: 0.01%, 0.05%, 0.3%, 1%
  feeTiers: [100, 500, 2500, 10000], // 0.01%, 0.05%, 0.25%, 1%
  trustedTokens: [
    '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', // WBNB
    '0x55d398326f99059fF775485246999027B3197955', // USDT
    '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', // USDC
  ],
};
